import React from 'react'
import FormControl from '@mui/material/FormControl'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Card from '@mui/material/Card'
import Header, { HeaderVariant } from '../Elements/Header'
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from '@mui/material'
import { styled } from '@mui/system'

export const CheckboxFormGroup = styled(FormControl)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1),
  justifyContent: 'flex-start',
})) as typeof FormControl

const CheckboxListFormCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3, 4, 4),
  width: '100%',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
}))

export type CheckboxItems = {
  key: number
  name: string
  checked: boolean
}

export interface CheckboxListCardProps {
  checkboxItems?: CheckboxItems[]
  isFieldDisabled: boolean
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  headerId: string
  headerName: string
  alertMessage?: string
  headerCheckbox?: CheckboxItems
}

const CheckboxListCard: React.FC<CheckboxListCardProps> = ({
  checkboxItems,
  isFieldDisabled,
  handleCheckboxChange,
  headerId,
  headerName,
  alertMessage,
  headerCheckbox,
}) => {
  const theme = useTheme()
  return (
    <CheckboxListFormCard>
      <Box display={'flex'} alignItems={'flex-start'}>
        <Header
          id={headerId}
          headerName={headerName}
          component="h2"
          variant={HeaderVariant.Card}
        />
        {!!headerCheckbox && (
          <Box pl={3}>
            <FormControlLabel
              sx={{
                lineHeight: 1.4,
              }}
              control={
                <Checkbox
                  data-testid="content-tag-checkbox"
                  sx={{
                    color: theme.palette.textOrIcon.checkbox,
                    padding: theme.spacing(0.2, 0.8, 0.2, 0),
                  }}
                  size="medium"
                  onChange={handleCheckboxChange}
                  key={`${headerCheckbox?.name}-${headerCheckbox?.key}`}
                  name={headerCheckbox?.name}
                  value={headerCheckbox?.key}
                  inputProps={{ 'aria-labelledby': `${headerCheckbox?.name}` }}
                  disabled={isFieldDisabled}
                  checked={headerCheckbox?.checked}
                />
              }
              label={headerCheckbox?.name}
            />
          </Box>
        )}
      </Box>
      {alertMessage && (
        <Alert severity="error">
          <Typography variant="subtitle2" component="p">
            {alertMessage}
          </Typography>
        </Alert>
      )}
      <CheckboxFormGroup component="fieldset">
        <FormGroup>
          {checkboxItems ? (
            <Grid
              container
              sx={{
                [theme.breakpoints.up('md')]: {
                  maxHeight: theme.spacing(71.5),
                },
                width: '100%',
                overflowY: 'auto',
                paddingLeft: theme.spacing(1.5),
              }}
              direction="column"
            >
              {checkboxItems &&
                checkboxItems.map((item) => (
                  <Grid item key={item.key}>
                    <FormControlLabel
                      sx={{
                        lineHeight: 1.4,
                      }}
                      control={
                        <Checkbox
                          data-testid="content-tag-checkbox"
                          sx={{
                            color: theme.palette.textOrIcon.checkbox,
                            padding: theme.spacing(0.2, 0.8, 0.2, 0),
                          }}
                          size="medium"
                          onChange={handleCheckboxChange}
                          key={`${item.name}-${item.key}`}
                          name={item.name}
                          value={item.key}
                          inputProps={{ 'aria-labelledby': `${item.name}` }}
                          disabled={isFieldDisabled}
                          checked={item.checked}
                        />
                      }
                      label={item.name}
                    />
                  </Grid>
                ))}
            </Grid>
          ) : (
            <Box
              sx={{
                display: 'flex',
                height: '100%',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </FormGroup>
      </CheckboxFormGroup>
    </CheckboxListFormCard>
  )
}
export default CheckboxListCard
